// Utility functions for JWT token management

/**
 * Check if a JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} - True if token is expired or invalid, false otherwise
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    
    // Check if token has expiration
    if (!decoded.exp) return false;
    
    // Compare expiration time with current time
    const currentTime = Math.floor(Date.now() / 1000);
    return decoded.exp < currentTime;
  } catch (error) {
    console.error('Error decoding token:', error);
    return true; // If we can't decode, assume it's expired
  }
};

/**
 * Get token expiration time in seconds
 * @param {string} token - The JWT token
 * @returns {number|null} - Expiration time in seconds, or null if invalid
 */
export const getTokenExpiration = (token) => {
  if (!token) return null;
  
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.exp || null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Get time until token expires in seconds
 * @param {string} token - The JWT token
 * @returns {number|null} - Seconds until expiration, or null if invalid/expired
 */
export const getTimeUntilExpiration = (token) => {
  const exp = getTokenExpiration(token);
  if (!exp) return null;
  
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiration = exp - currentTime;
  
  return timeUntilExpiration > 0 ? timeUntilExpiration : null;
};

/**
 * Get session progress (percent of time remaining). Uses iat/exp from JWT if available.
 * @param {string} token - The JWT token
 * @returns {{ percentRemaining: number } | null} - Percent 0-100, or null if invalid/expired
 */
export const getTokenSessionProgress = (token) => {
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    const exp = decoded.exp;
    const iat = decoded.iat ?? exp - 86400; // fallback: assume 24h if no iat
    if (!exp) return null;
    const now = Math.floor(Date.now() / 1000);
    if (now >= exp) return null;
    const total = exp - iat;
    const remaining = exp - now;
    const percentRemaining = Math.min(100, Math.max(0, (remaining / total) * 100));
    return { percentRemaining };
  } catch (e) {
    return null;
  }
};

/**
 * Format time until expiration in a human-readable format
 * @param {string} token - The JWT token
 * @returns {string} - Formatted time string
 */
export const getFormattedTimeUntilExpiration = (token) => {
  const seconds = getTimeUntilExpiration(token);
  if (!seconds) return 'Expired';
  
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const days = Math.floor(hours / 24);
  
  if (days > 0) {
    const remainingHours = hours % 24;
    if (remainingHours > 0) {
      return `${days} day${days > 1 ? 's' : ''} and ${remainingHours} hour${remainingHours > 1 ? 's' : ''}`;
    } else {
      return `${days} day${days > 1 ? 's' : ''}`;
    }
  } else if (hours > 0) {
    if (remainingMinutes > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} and ${remainingMinutes} min${remainingMinutes > 1 ? 's' : ''}`;
    } else {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }
};

/**
 * Clear all authentication data and redirect to login
 * @param {string} message - Optional message to display on login page
 */
export const clearAuthAndRedirect = (message = 'Your session has expired. Please login again.') => {
  // Clear all localStorage
  localStorage.clear();
  
  // Clear all sessionStorage
  sessionStorage.clear();
  
  // Clear all cookies
  document.cookie.split(";").forEach(function(c) {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  // Clear domain-specific cookies
  const domain = window.location.hostname;
  document.cookie.split(";").forEach(function(c) {
    document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/;domain=" + domain);
  });
  
  // Clear subdomain cookies if any
  if (domain.indexOf('.') > -1) {
    const rootDomain = '.' + domain.split('.').slice(-2).join('.');
    document.cookie.split(";").forEach(function(c) {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/;domain=" + rootDomain);
    });
  }
  
  // Redirect to login with message
  window.location.href = `/login?message=${encodeURIComponent(message)}`;
};
